import { fetchWithDedup } from "./requestCache";

// Authentication utilities for token and profile management
export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  role: string;
  isActive: boolean;
  emailVerified: boolean;
  googleId?: string;
  facebookId?: string;
  createdAt: string;
  updatedAt: string;
}

export class AuthService {
  private static readonly ACCESS_TOKEN_KEY = "accessToken";
  private static readonly REFRESH_TOKEN_KEY = "refreshToken";
  private static readonly USER_KEY = "userProfile";
  private static readonly ACTIVE_TAB_KEY = "dashboard-active-tab";

  // Token Management
  static setTokens(tokens: AuthTokens): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refreshToken);
  }

  static getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  static clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.ACTIVE_TAB_KEY);
  }

  static isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  // User Profile Management
  static setUserProfile(user: UserProfile): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  static getUserProfile(): UserProfile | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }

  static clearUserProfile(): void {
    localStorage.removeItem(this.USER_KEY);
  }

  // API Request Helper with Token
  static async makeAuthenticatedRequest(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      throw new Error("No access token available");
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle token expiration
    if (response.status === 401) {
      const refreshSuccess = await this.refreshToken();
      if (refreshSuccess) {
        // Retry the request with new token
        const newToken = this.getAccessToken();
        const retryHeaders = {
          ...headers,
          Authorization: `Bearer ${newToken}`,
        };

        return fetch(url, {
          ...options,
          headers: retryHeaders,
        });
      } else {
        // Refresh failed, redirect to login
        this.logout();
        window.location.href = "/login";
        throw new Error("Authentication failed");
      }
    }

    // Handle banned user
    if (response.status === 403) {
      try {
        const cloned = response.clone();
        const payload = await cloned.json();
        if (payload && payload.message === "User is banned") {
          // Set a flag to show a toast on next page render
          localStorage.setItem("banned_notice", "1");
          // Clear local session and redirect to login (preserve current lang segment)
          await this.logout();
          const seg = (
            window.location.pathname.split("/")[1] || "fr"
          ).toLowerCase();
          window.location.href = `/${seg}/login`;
        }
      } catch {
        // ignore json parsing errors
      }
    }

    return response;
  }

  // Token Refresh
  static async refreshToken(): Promise<boolean> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        return false;
      }

      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000";
      const response = await fetch(`${apiUrl}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          this.setTokens({
            accessToken: data.data.accessToken,
            refreshToken: data.data.refreshToken,
          });
          if (data.data.user) {
            this.setUserProfile(data.data.user);
          }
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error("Token refresh failed:", error);
      return false;
    }
  }

  // Profile Management - deduplicated (no caching for dynamic user data)
  private static readonly PROFILE_DEDUP_KEY = "auth_profile";

  static async fetchProfile(): Promise<UserProfile | null> {
    try {
      const profile = await fetchWithDedup(this.PROFILE_DEDUP_KEY, async () => {
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000";
        const response = await this.makeAuthenticatedRequest(
          `${apiUrl}/auth/profile`
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data && data.data.user) {
            this.setUserProfile(data.data.user);
            return data.data.user as UserProfile;
          }
        }
        return null;
      });
      return profile;
    } catch (error) {
      console.error("Profile fetch failed:", error);
      return null;
    }
  }

  static async updateProfile(
    profileData: Partial<UserProfile>
  ): Promise<boolean> {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000";
      const response = await this.makeAuthenticatedRequest(
        `${apiUrl}/auth/profile`,
        {
          method: "PUT",
          body: JSON.stringify(profileData),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && data.data.user) {
          this.setUserProfile(data.data.user);
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error("Profile update failed:", error);
      return false;
    }
  }

  static async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<boolean> {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000";
      const response = await this.makeAuthenticatedRequest(
        `${apiUrl}/auth/change-password`,
        {
          method: "POST",
          body: JSON.stringify({
            currentPassword,
            newPassword,
          }),
        }
      );

      return response.ok;
    } catch (error) {
      console.error("Password change failed:", error);
      return false;
    }
  }

  // Logout
  static async logout(): Promise<void> {
    try {
      const refreshToken = this.getRefreshToken();
      if (refreshToken) {
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000";
        // Attempt to revoke token on server (optional)
        await this.makeAuthenticatedRequest(`${apiUrl}/auth/logout`, {
          method: "POST",
          body: JSON.stringify({ refreshToken }),
        });
      }
    } catch (error) {
      console.error("Logout request failed:", error);
    } finally {
      // Always clear local storage
      this.clearTokens();
      this.clearUserProfile();
    }
  }

  // Token Expiration Check
  static isTokenExpiringSoon(): boolean {
    const token = this.getAccessToken();
    if (!token) return true;

    try {
      // Decode JWT payload (without verification for client-side check)
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiry = expirationTime - currentTime;

      // Return true if token expires in less than 5 minutes
      return timeUntilExpiry < 5 * 60 * 1000;
    } catch (error) {
      console.error("Token parsing failed:", error);
      return true;
    }
  }

  // Auto-refresh token if expiring soon
  static async ensureValidToken(): Promise<boolean> {
    if (!this.isAuthenticated()) {
      return false;
    }

    if (this.isTokenExpiringSoon()) {
      return await this.refreshToken();
    }

    return true;
  }
}
